import { useEffect, useMemo, type ReactNode } from "react";

import { useTenantBranding } from "@/api/queries/branding";
import { tenantAccentStyle } from "@/lib/tenantBranding";
import { SITE_THEME_FONTS } from "@/shell/siteThemeFonts";
import {
  effectiveSectionVariants,
  findSiteTheme,
  siteThemeAttributes,
  siteThemeCss,
  type SiteThemeKey,
} from "@/shell/siteThemes";
import { useShellNav } from "@/shell/useShellNav";
import "@/shell/siteThemes.css";

const DEFAULT_FAVICONS = [
  { href: "/favicon.svg", type: "image/svg+xml" },
  { href: "/forgmatic-logo.png", type: "image/png", sizes: "any" },
];
const DEFAULT_APPLE_TOUCH_ICON = "/forgmatic-logo.png";

function appendIconLink({
  href,
  sizes,
  type,
}: {
  href: string;
  sizes?: string;
  type?: string;
}) {
  const link = document.createElement("link");
  link.setAttribute("rel", "icon");
  link.setAttribute("href", href);
  if (type) link.setAttribute("type", type);
  if (sizes) link.setAttribute("sizes", sizes);
  document.head.appendChild(link);
}

function setFaviconLinks(logoUrl?: string | null) {
  document.head
    .querySelectorAll<HTMLLinkElement>('link[rel="icon"]')
    .forEach((link) => link.remove());

  if (logoUrl) {
    appendIconLink({ href: logoUrl });
  } else {
    DEFAULT_FAVICONS.forEach(appendIconLink);
  }

  document.head
    .querySelectorAll<HTMLLinkElement>('link[rel="apple-touch-icon"]')
    .forEach((link) => link.remove());
  const appleTouchIcon = document.createElement("link");
  appleTouchIcon.setAttribute("rel", "apple-touch-icon");
  appleTouchIcon.setAttribute("href", logoUrl || DEFAULT_APPLE_TOUCH_ICON);
  document.head.appendChild(appleTouchIcon);
}

export function TenantBrandTheme({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const shellNav = useShellNav();
  const isTenant = shellNav.shell === "tenant";
  const branding = useTenantBranding({ enabled: isTenant });
  const appearance = isTenant ? branding.data?.site_appearance : undefined;
  const theme = findSiteTheme(appearance?.theme);
  const primaryColor = branding.data?.primary_color;
  // A theme derives the accent itself (with a contrast check against its
  // own surfaces), so the plain accent style applies only without one —
  // an inline style would otherwise beat the theme's stylesheet.
  const style = isTenant && !theme ? tenantAccentStyle(primaryColor) : {};
  const themeCss = useMemo(
    () => (theme ? siteThemeCss(theme, primaryColor) : null),
    [theme, primaryColor],
  );
  const variants = effectiveSectionVariants(theme, appearance?.sections);

  useEffect(() => {
    setFaviconLinks(isTenant ? branding.data?.logo_url : null);
  }, [isTenant, branding.data?.logo_url]);

  useEffect(() => {
    if (theme) void SITE_THEME_FONTS[theme.key as SiteThemeKey]?.();
  }, [theme]);

  // Mirrored on <html>: modals and dropdowns render in portals outside
  // this wrapper, and the page background under short pages is the
  // body's. The edge middleware writes the same attributes into the
  // served HTML so the first paint is already themed; this keeps them in
  // step with a change made in the editor and removes them when the
  // storefront unmounts (e.g. into the admin shell) — or when branding
  // says there is no theme — so the admin never inherits one.
  const attrs = theme ? siteThemeAttributes(theme, appearance?.sections) : null;
  const attrsKey = attrs ? JSON.stringify(attrs) : "";
  useEffect(() => {
    if (!isTenant || !branding.isSuccess) return undefined;
    const html = document.documentElement;
    const names = ["data-site-theme", "data-hero", "data-steps", "data-benefits", "data-page-hero"];
    const next = attrsKey ? (JSON.parse(attrsKey) as Record<string, string>) : {};
    for (const name of names) {
      if (next[name]) html.setAttribute(name, next[name]);
      else html.removeAttribute(name);
    }
    return () => {
      for (const name of names) html.removeAttribute(name);
    };
  }, [isTenant, branding.isSuccess, attrsKey]);

  return (
    <div
      style={style}
      data-testid={isTenant ? "tenant-brand-theme" : undefined}
      data-site-theme={theme?.key}
      data-hero={variants.hero}
      data-steps={variants.steps}
      data-benefits={variants.benefits}
      data-page-hero={variants.page_hero}
      className={theme ? "bg-bg font-sans text-ink" : undefined}
    >
      {themeCss && <style data-testid="site-theme-css">{themeCss}</style>}
      {children}
    </div>
  );
}

